import type { Anomaly, AnomalyDirection, MetricPolarity } from '../models/anomaly.types'

import { useMetricCatalog, useReportCellFormatter } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

export interface RenderedAnomalyNarrative {
  title: string
  description: string
}

export interface UseAnomalyFormat {
  formatMetric: (value: number, metricField: string) => string
  formatMetricFull: (value: number, metricField: string) => string
  isPositiveTrend: (polarity: MetricPolarity, direction: AnomalyDirection) => boolean
  narrative: (anomaly: Anomaly) => RenderedAnomalyNarrative
}

const NUMERIC_RUN = /-?\d+(?:[.,\u00a0\u202f]\d+)*/u

export function useAnomalyFormat(): UseAnomalyFormat {
  const catalog = useMetricCatalog()
  const cellFormatter = useReportCellFormatter()
  const format = useFormat()
  const { t } = useI18n()

  function formatMetricFull(value: number, metricField: string): string {
    const column = catalog.getColumn(metricField) ?? {
      field: metricField,
      header: catalog.metricLabel(metricField),
      numeric: true,
    }
    return cellFormatter.formatCell(value, column)
  }

  function formatMetric(value: number, metricField: string): string {
    const full = formatMetricFull(value, metricField)
    if (Math.abs(value) < 1000) return full
    const compact = format.formatCompact(Math.abs(value), { maxFrac: 2 })
    return full.replace(NUMERIC_RUN, (numeric) => `${numeric.startsWith('-') ? '-' : ''}${compact}`)
  }

  function isPositiveTrend(polarity: MetricPolarity, direction: AnomalyDirection): boolean {
    return polarity === 'lower_is_better' ? direction === 'DOWN' : direction === 'UP'
  }

  function narrative(anomaly: Anomaly): RenderedAnomalyNarrative {
    return {
      title: t(anomaly.narrative.titleKey, anomaly.narrative.titleParams),
      description: t(anomaly.narrative.descriptionKey, anomaly.narrative.descriptionParams),
    }
  }

  return { formatMetric, formatMetricFull, isPositiveTrend, narrative }
}
